"use client";

import { useEffect, useState } from "react";
import { useLingui } from "@lingui/react/macro";

import {
  Header as SharedComponentsHeader,
  type DesktopBreakpoint,
} from "@marketing-sites/ui/components";
import { useHeaderNavigation } from "./fixtures";
import { useFormFieldPersistence } from "@marketing-sites/hooks/use-persist-form-fields";
import { EXTERNAL_ROUTES } from "@/constants/external-routes";
import { useLogicalPathname } from "@/lib/l10n/providers";
import { localeConfig } from "@/lib/l10n/config";
import { TrialCtaNavButton } from "./trial-cta-nav-button";
import { Saans, SaansMono } from "@/lib/fonts";
import { cn } from "@marketing-sites/utils/cn";

// The desktop row needs 1271px in German and 1260px in French; es-419 (1218px), pt-BR (1159px) and English (1028px) fit under 1230.
const WIDE_NAV_LOCALES = new Set(["de-DE", "fr-FR"]);

function desktopBreakpointFor(locale: string): DesktopBreakpoint {
  return WIDE_NAV_LOCALES.has(locale) ? "wide" : "default";
}

export function Header({
  className,
}: {
  isDarkPage?: boolean;
  className?: string;
}) {
  const pathname = useLogicalPathname();
  const { t, i18n } = useLingui();
  const {
    intercomFixtureData,
    intercomMobileMenuData,
    viewDemosFixtureData,
    viewDemosSmallCompanyFixtureData,
    viewDemosZendeskFlowFixtureData,
  } = useHeaderNavigation();

  // --------------------------------------------------------------------------
  // VIEW DEMOS HEADER MANIPULATION
  // --------------------------------------------------------------------------
  const { stringValue: numberOfEmployees } = useFormFieldPersistence(
    "numEmployeesIntercom",
  );
  // Check if the View Demos form has been submitted
  const { stringValue: gateSubmitted } = useFormFieldPersistence(
    "view-demos-gate-submitted",
  );

  // /view-demos uses a reduced header (no primary nav). Companies with ≤15
  // employees get a further reduction to only "Start free trial".
  // The interim ja-jp dir mounts no LocaleProvider, so its logical path keeps the prefix.
  const isViewDemos =
    pathname.startsWith("/view-demos") ||
    pathname.startsWith("/ja-jp/view-demos");
  const [numberOfEmployeesParam, setNumberOfEmployeesParam] = useState<
    string | null
  >(null);
  const [isZendeskFlow, setIsZendeskFlow] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setNumberOfEmployeesParam(params.get("numberOfEmployees"));
    setIsZendeskFlow(params.get("formSource") === "fin2_zendesk");
  }, []);

  // Check if the company is a small company via URL param OR when the form gate has been submitted
  const isSmallCompany =
    isViewDemos &&
    ((!!gateSubmitted && !!numberOfEmployees && +numberOfEmployees <= 15) ||
      (numberOfEmployeesParam !== null && +numberOfEmployeesParam <= 15));

  // --------------------------------------------------------------------------

  // DRLP routes render their own header (see
  // app/[locale]/(i2y4)/drlp/_lib/drlp-header.tsx) so editors can override the nav CTAs
  // per page — the global header steps aside here. Placed after all hooks so the
  // Rules of Hooks are respected. `/legacy-drlp/` is the internal rewrite target
  // for legacy-only slugs and must match too.
  if (pathname.startsWith("/drlp/") || pathname.startsWith("/legacy-drlp/"))
    return null;

  const baseFixtureData = isZendeskFlow
    ? viewDemosZendeskFlowFixtureData
    : isSmallCompany
      ? viewDemosSmallCompanyFixtureData
      : isViewDemos
        ? viewDemosFixtureData
        : intercomFixtureData;

  // The nav signup CTA targets the React signup app on every route. That URL is
  // same-origin, so it must render as a hard <a> (TrialCtaNavButton) — the shared
  // Header would otherwise route it through next/link and SPA-intercept the click,
  // bypassing the AddUrlParamsToCrossSiteLinks forwarder. Destination and label
  // vary by page: the startup programs use their "Apply now" signups, home keeps
  // the Suite trial, everything else the generic trial.
  const navCta: { href: string; label: string; analyticsLabel: string } =
    pathname === "/startups-program"
      ? {
          href: EXTERNAL_ROUTES.REACT_STARTUP_START_FREE_TRIAL,
          label: t`Apply now`,
          analyticsLabel: "Apply now",
        }
      : pathname === "/early-stage"
        ? {
            href: EXTERNAL_ROUTES.REACT_STARTUPS_APPLY,
            label: t`Apply now`,
            analyticsLabel: "Apply now",
          }
        : pathname === "/"
          ? {
              href: EXTERNAL_ROUTES.REACT_SUITE_START_FREE_TRIAL,
              label: t`Start free trial`,
              analyticsLabel: "Start free trial",
            }
          : {
              href: EXTERNAL_ROUTES.REACT_GENERIC_START_FREE_TRIAL,
              label: t`Start free trial`,
              analyticsLabel: "Start free trial",
            };

  // Build new fixture data immutably with the updated trial href
  const fixtureData = {
    ...baseFixtureData,
    banner: pathname === "/" ? intercomFixtureData.banner : undefined,
    secondary: baseFixtureData.secondary.map((item) => {
      if (
        item.type !== "button" ||
        item.href !== EXTERNAL_ROUTES.REACT_GENERIC_START_FREE_TRIAL
      ) {
        return item;
      }
      return {
        type: "custom" as const,
        customItem: (
          <TrialCtaNavButton
            href={navCta.href}
            label={navCta.label}
            analyticsLabel={navCta.analyticsLabel}
            buttonMetadata={{ analyticsId: "btn_nav_trial_cta" }}
            eventMetadata={{ analyticsId: "evt_nav_trial_cta" }}
            className="h-10 gap-1 rounded-[4px] px-3 py-0 text-[14px] leading-[1.1]"
          />
        ),
      };
    }),
  };

  const mobileMenuData = {
    ...intercomMobileMenuData,
    tertiary: intercomMobileMenuData.tertiary?.map((item) =>
      item.type === "button" &&
      item.href === EXTERNAL_ROUTES.REACT_GENERIC_START_FREE_TRIAL
        ? {
            type: "custom" as const,
            customItem: (
              <TrialCtaNavButton
                href={navCta.href}
                label={navCta.label}
                analyticsLabel={navCta.analyticsLabel}
                buttonMetadata={{ analyticsId: "btn_nav_drawer_trial_cta" }}
                eventMetadata={{ analyticsId: "evt_nav_drawer_trial_cta" }}
                className="w-full"
              />
            ),
          }
        : item,
    ),
  };

  return (
    <SharedComponentsHeader
      variant="floating"
      homeDomain="intercom.com"
      desktopBreakpoint={desktopBreakpointFor(i18n.locale)}
      translatedLabels={i18n.locale !== localeConfig.sourceLocale}
      fixtureData={fixtureData}
      mobileMenuFixtureData={mobileMenuData}
      disableMobileMenu={false}
      className={cn(
        "brand-i2y4",
        Saans.variable,
        SaansMono.variable,
        "font-sans",
        "[--nav-bar-bg:var(--color-white)] [--nav-keyline-accent:var(--color-helpdesk)]",
        className,
      )}
      bannerLinkMetadata={{ analyticsId: "evt_nav_banner_link" }}
      bannerCloseMetadata={{ analyticsId: "evt_nav_banner_close" }}
      desktopLogoMetadata={{ analyticsId: "link_nav_desktop_logo" }}
      switchHoverCardMetadata={{
        analyticsId: "link_nav_desktop_switch_hover_card",
      }}
      mobileLogoMetadata={{ analyticsId: "link_nav_mobile_logo" }}
      mobileCrossSiteMetadata={{ analyticsId: "link_nav_mobile_cross_site" }}
    />
  );
}
